import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconEdit, IconSpinner, IconTrash } from '../components/ActionIcons';
import { Pagination } from '../components/Pagination';
import { productApi, productCategoryApi } from '../api/api';
import { useStore } from '../context/StoreContext';
import { productCategoryLabelFromRow } from '../utils/productCategoryLabel';
import { swalSuccess, swalError, swalConfirmDelete } from '../utils/swal';

function toInputDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Product ilist returns `list_of_item` as a plain array (not Laravel paginator). */
function parseProductIlist(data) {
  const raw = data?.returnData?.list_of_item;
  if (Array.isArray(raw)) return { items: raw };
  if (raw?.data && Array.isArray(raw.data)) {
    return {
      items: raw.data,
      total: raw.total,
      lastPage: raw.last_page,
    };
  }
  return { items: [] };
}

function emptyAddProductForm() {
  return {
    name: '',
    categoryId: '',
    price: '',
    buyingPrice: '',
    reorderLevel: '',
    quantity: '',
    registeredDate: toInputDateString(new Date()),
  };
}

function emptyProductForm() {
  return {
    name: '',
    price: '',
    categoryId: '',
    reorderLevel: '',
    quantity: '',
    registeredDate: toInputDateString(new Date()),
    buyingPrice: '',
  };
}

function mapRowToProductForm(row) {
  const bal = row.balance ?? row.quantity;
  const qtyStr =
    bal != null && bal !== '' && !Number.isNaN(Number(bal))
      ? String(Math.max(0, Math.floor(Number(bal))))
      : '';
  let registeredDate = toInputDateString(new Date());
  if (row.registered_date != null && row.registered_date !== '') {
    const s = String(row.registered_date);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) registeredDate = s.slice(0, 10);
  }
  return {
    name: String(row.name ?? ''),
    price:
      row.selling_price != null && row.selling_price !== ''
        ? String(row.selling_price)
        : '',
    buyingPrice:
      row.buying_price != null && row.buying_price !== ''
        ? String(row.buying_price)
        : '',
    categoryId:
      row.category_id != null && row.category_id !== ''
        ? String(row.category_id)
        : '',
    reorderLevel:
      row.reorder_level != null && row.reorder_level !== ''
        ? String(row.reorder_level)
        : '',
    quantity: qtyStr,
    registeredDate,
  };
}

function parseCategoryIlist(data) {
  const root = data?.returnData?.list_of_item;
  if (!root) {
    return {
      items: [],
      total: 0,
      lastPage: 1,
      currentPage: 1,
      from: 0,
      to: 0,
    };
  }
  const items = root.data ?? [];
  return {
    items,
    total: root.total ?? items.length,
    lastPage: Math.max(1, root.last_page ?? 1),
    currentPage: root.current_page ?? 1,
    from: root.from ?? 0,
    to: root.to ?? 0,
  };
}

export function ProductManagement() {
  const navigate = useNavigate();
  const { addProduct, syncProductsFromServer } = useStore();
  const [tab, setTab] = useState('products');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(() => emptyAddProductForm());
  const [productSubmitError, setProductSubmitError] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [editProductUuid, setEditProductUuid] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyProductForm());
  const [editSubmitError, setEditSubmitError] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [productActionError, setProductActionError] = useState(null);

  const [prodItems, setProdItems] = useState([]);
  const [prodListLoading, setProdListLoading] = useState(false);
  const [prodListError, setProdListError] = useState(null);
  const [prodListPage, setProdListPage] = useState(1);
  const [prodPerPage] = useState(10);
  const [prodSearchInput, setProdSearchInput] = useState('');
  const [prodSearchQ, setProdSearchQ] = useState('');

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryLoadError, setCategoryLoadError] = useState(null);

  const [catItems, setCatItems] = useState([]);
  const [catListLoading, setCatListLoading] = useState(false);
  const [catListError, setCatListError] = useState(null);
  const [catListPage, setCatListPage] = useState(1);
  const [catPerPage] = useState(5);
  const [catSearchInput, setCatSearchInput] = useState('');
  const [catSearchQ, setCatSearchQ] = useState('');
  const [catMeta, setCatMeta] = useState({
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [catError, setCatError] = useState(null);
  const [catSubmitting, setCatSubmitting] = useState(false);
  const [catEditingId, setCatEditingId] = useState(null);
  const [catEditDraft, setCatEditDraft] = useState({ name: '', description: '' });
  const [catDeletingId, setCatDeletingId] = useState(null);
  const [catActionError, setCatActionError] = useState(null);

  const fetchCategoryList = useCallback(async () => {
    setCatListLoading(true);
    setCatListError(null);
    try {
      const resp = await productCategoryApi.ilist({
        paginate: true,
        per_page: catPerPage,
        page: catListPage,
        q: catSearchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setCatItems([]);
        setCatMeta({ total: 0, lastPage: 1, from: 0, to: 0 });
        setCatListError(data?.errorMessage || 'Failed to load categories.');
        return;
      }
      const parsed = parseCategoryIlist(data);
      setCatItems(parsed.items);
      setCatMeta({
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while loading categories.';
      setCatItems([]);
      setCatMeta({ total: 0, lastPage: 1, from: 0, to: 0 });
      setCatListError(msg);
    } finally {
      setCatListLoading(false);
    }
  }, [catListPage, catPerPage, catSearchQ]);

  const fetchProductList = useCallback(async () => {
    setProdListLoading(true);
    setProdListError(null);
    try {
      const resp = await productApi.ilist({
        paginate: true,
        per_page: prodPerPage,
        page: prodListPage,
        q: prodSearchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setProdItems([]);
        setProdListError(data?.errorMessage || 'Failed to load products.');
        return;
      }
      const parsed = parseProductIlist(data);
      setProdItems(parsed.items);
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while loading products.';
      setProdItems([]);
      setProdListError(msg);
    } finally {
      setProdListLoading(false);
    }
  }, [prodListPage, prodPerPage, prodSearchQ]);

  const fetchFullCatalogSync = useCallback(async () => {
    const all = [];
    let page = 1;
    const per = 100;
    try {
      for (;;) {
        const resp = await productApi.ilist({
          paginate: true,
          per_page: per,
          page,
        });
        const data = resp?.data ?? null;
        if (!data || data.status !== 'OK') break;
        const parsed = parseProductIlist(data);
        const items = parsed.items;
        if (!items.length) break;
        all.push(...items);
        if (items.length < per) break;
        page += 1;
      }
      syncProductsFromServer(all);
    } catch {
      // ignore sync failure; table still shows current page
    }
  }, [syncProductsFromServer]);

  useEffect(() => {
    if (tab !== 'categories') return;
    fetchCategoryList();
  }, [tab, fetchCategoryList]);

  useEffect(() => {
    if (tab !== 'products') return;
    fetchProductList();
  }, [tab, fetchProductList]);

  useEffect(() => {
    if (tab !== 'products') return;
    fetchFullCatalogSync();
  }, [tab, fetchFullCatalogSync]);

  function resetAddForm() {
    setForm(emptyAddProductForm());
    setProductSubmitError(null);
  }

  function closeAddModal() {
    setAddOpen(false);
    resetAddForm();
  }

  const loadCategoryOptions = useCallback(async () => {
    setCategoryLoading(true);
    setCategoryLoadError(null);
    try {
      const resp = await productCategoryApi.ilist({
        paginate: true,
        per_page: 100,
        page: 1,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setCategoryOptions([]);
        setCategoryLoadError(data?.errorMessage || 'Failed to load categories.');
        return;
      }
      const parsed = parseCategoryIlist(data);
      setCategoryOptions(parsed.items ?? []);
    } catch (err) {
      setCategoryOptions([]);
      setCategoryLoadError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading categories.'
      );
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!addOpen) return;
    loadCategoryOptions();
  }, [addOpen, loadCategoryOptions]);

  useEffect(() => {
    if (!editOpen) return;
    loadCategoryOptions();
  }, [editOpen, loadCategoryOptions]);

  async function handleAddSubmit(e) {
    e.preventDefault();
    setProductSubmitError(null);
    const price = parseFloat(form.price, 10);
    const categoryIdNum = Number(form.categoryId);
    const qty = Math.max(0, Math.floor(Number(form.quantity) || 0));
    const buying = Math.max(0, Number(form.buyingPrice) || 0);
    if (!form.name.trim() || Number.isNaN(price) || price < 0) return;
    if (!categoryIdNum || Number.isNaN(categoryIdNum)) {
      setProductSubmitError('Category is required.');
      return;
    }
    if (form.quantity !== '' && form.quantity != null) {
      const q = Number(form.quantity);
      if (Number.isNaN(q) || q < 0) {
        setProductSubmitError('Quantity must be zero or a positive number.');
        return;
      }
    }

    const registeredDate = String(form.registeredDate ?? '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(registeredDate)) {
      setProductSubmitError('Registered date must be a valid date (YYYY-MM-DD).');
      return;
    }

    try {
      const resp = await productApi.save({
        name: form.name.trim(),
        category_id: categoryIdNum,
        selling_price: price,
        buying_price: buying,
        reorder_level: Math.max(0, Math.floor(Number(form.reorderLevel) || 0)),
        quantity: qty,
        registered_date: registeredDate,
      });

      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to save product.';
        setProductSubmitError(msg);
        swalError('Could not save product', msg);
        return;
      }

      const serverRow = data.returnData;
      const categoryName =
        categoryOptions.find((c) => Number(c.id) === categoryIdNum)?.name ??
        'General';

      addProduct({
        id: serverRow?.id != null ? String(serverRow.id) : undefined,
        name: serverRow?.name ?? form.name.trim(),
        price,
        barcode:
          serverRow?.barcode != null
            ? String(serverRow.barcode).trim()
            : undefined,
        category: categoryName,
        initialStock: qty,
        serverProductId: serverRow?.id,
        serverProductUuid: serverRow?.uuid,
        categoryId: categoryIdNum,
        code: serverRow?.code,
        uom: serverRow?.uom,
      });
      closeAddModal();
      fetchProductList();
      fetchFullCatalogSync();
      swalSuccess('Product added', 'The product was saved.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while saving product.';
      setProductSubmitError(msg);
      swalError('Could not save product', msg);
    }
  }

  function closeEditModal() {
    setEditOpen(false);
    setEditProductId(null);
    setEditProductUuid(null);
    setEditForm(emptyProductForm());
    setEditSubmitError(null);
    setEditSubmitting(false);
  }

  function openEditProduct(row) {
    setProductActionError(null);
    setEditProductId(row.id);
    setEditProductUuid(row.uuid ?? null);
    setEditForm(mapRowToProductForm(row));
    setEditSubmitError(null);
    setEditOpen(true);
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    setEditSubmitError(null);
    const price = parseFloat(editForm.price, 10);
    const categoryIdNum = Number(editForm.categoryId);
    const qty = Math.max(0, Math.floor(Number(editForm.quantity) || 0));
    const buying = Math.max(0, Number(editForm.buyingPrice) || 0);
    if (!editForm.name.trim() || Number.isNaN(price) || price < 0) return;
    if (!categoryIdNum || Number.isNaN(categoryIdNum)) {
      setEditSubmitError('Category is required.');
      return;
    }
    if (editForm.quantity !== '' && editForm.quantity != null) {
      const q = Number(editForm.quantity);
      if (Number.isNaN(q) || q < 0) {
        setEditSubmitError('Quantity must be zero or a positive number.');
        return;
      }
    }
    const registeredDate = String(editForm.registeredDate ?? '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(registeredDate)) {
      setEditSubmitError('Registered date must be a valid date (YYYY-MM-DD).');
      return;
    }
    if (editProductId == null) return;

    setEditSubmitting(true);
    try {
      const resp = await productApi.update({
        id: editProductId,
        ...(editProductUuid ? { uuid: editProductUuid } : {}),
        name: editForm.name.trim(),
        category_id: categoryIdNum,
        selling_price: price,
        buying_price: buying,
        reorder_level: Math.max(0, Math.floor(Number(editForm.reorderLevel) || 0)),
        quantity: qty,
        registered_date: registeredDate,
      });

      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to update product.';
        setEditSubmitError(msg);
        swalError('Could not update product', msg);
        return;
      }

      closeEditModal();
      fetchProductList();
      fetchFullCatalogSync();
      swalSuccess('Product updated', 'Changes were saved.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while updating product.';
      setEditSubmitError(msg);
      swalError('Could not update product', msg);
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleDeleteProduct(row) {
    const confirmed = await swalConfirmDelete({
      title: 'Delete product?',
      text: `Delete "${row.name}"? This cannot be undone.`,
    });
    if (!confirmed) return;

    setProductActionError(null);
    setDeletingId(String(row.id));
    try {
      const resp = await productApi.delete({
        id: row.id,
        ...(row.uuid ? { uuid: row.uuid } : {}),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to delete product.';
        setProductActionError(msg);
        swalError('Could not delete product', msg);
        return;
      }
      fetchProductList();
      fetchFullCatalogSync();
      swalSuccess('Product deleted', 'The product was removed.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while deleting product.';
      setProductActionError(msg);
      swalError('Could not delete product', msg);
    } finally {
      setDeletingId(null);
    }
  }

  function applyProductSearch(e) {
    e.preventDefault();
    setProdSearchQ(prodSearchInput.trim());
    setProdListPage(1);
    setProductActionError(null);
  }

  function clearProductSearch() {
    setProdSearchInput('');
    setProdSearchQ('');
    setProdListPage(1);
    setProductActionError(null);
  }

  async function saveCategory({ id, uuid, name, description }) {
    const n = String(name ?? '').trim();
    if (!n) return { ok: false, error: 'Category name is required.' };
    setCatSubmitting(true);
    setCatError(null);
    try {
      const resp = await productCategoryApi.save({
        id,
        uuid,
        name: n,
        description: String(description ?? ''),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        return {
          ok: false,
          error: data?.errorMessage || 'Failed to save category.',
        };
      }
      const row = data.returnData;
      await fetchCategoryList();
      return { ok: true, row };
    } catch (err) {
      const data = err?.response?.data;
      return {
        ok: false,
        error: data?.errorMessage || 'Network error while saving category.',
      };
    } finally {
      setCatSubmitting(false);
    }
  }

  async function handleAddCategory(e) {
    e.preventDefault();
    const r = await saveCategory({
      name: catForm.name,
      description: catForm.description,
    });
    if (!r.ok) {
      setCatError(r.error);
      swalError('Could not save category', r.error);
      return;
    }
    setCatForm({ name: '', description: '' });
    setCatListPage(1);
    swalSuccess('Category added', 'The category was saved.');
  }

  function startCategoryEdit(row) {
    setCatEditingId(row.id);
    setCatEditDraft({
      name: row.name ?? '',
      description: row.description ?? '',
    });
  }

  async function commitCategoryEdit(row) {
    const r = await saveCategory({
      id: row.id,
      uuid: row.uuid,
      name: catEditDraft.name,
      description: catEditDraft.description,
    });
    if (!r.ok) {
      setCatError(r.error);
      swalError('Could not update category', r.error);
      return;
    }
    setCatEditingId(null);
    swalSuccess('Category updated', 'Changes were saved.');
  }

  async function handleDeleteCategory(row) {
    const confirmed = await swalConfirmDelete({
      title: 'Delete category?',
      text: `Delete category "${row.name}"? Products using it may be affected.`,
    });
    if (!confirmed) return;

    setCatActionError(null);
    const rowKey = String(row.id);
    setCatDeletingId(rowKey);
    try {
      const resp = await productCategoryApi.delete({
        id: row.id,
        ...(row.uuid ? { uuid: row.uuid } : {}),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to delete category.';
        setCatActionError(msg);
        swalError('Could not delete category', msg);
        return;
      }
      setCatEditingId((prev) => (prev === row.id ? null : prev));
      await fetchCategoryList();
      loadCategoryOptions();
      swalSuccess('Category deleted', 'The category was removed.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while deleting category.';
      setCatActionError(msg);
      swalError('Could not delete category', msg);
    } finally {
      setCatDeletingId(null);
    }
  }

  function applyCategorySearch(e) {
    e.preventDefault();
    setCatSearchQ(catSearchInput.trim());
    setCatListPage(1);
    setCatActionError(null);
  }

  function clearCategorySearch() {
    setCatSearchInput('');
    setCatSearchQ('');
    setCatListPage(1);
    setCatActionError(null);
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">Product Management</h1>
        <p className="page-subtitle">
          Maintain the catalog. New stock starts at zero — adjust under Stock
          Management.
        </p>
      </header>

      <div className="card" style={{ marginBottom: '0.85rem' }}>
        <div className="card-toolbar" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'products' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('products')}
          >
            Products
          </button>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'categories' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('categories')}
          >
            Categories
          </button>
        </div>
      </div>

      {tab === 'categories' && (
        <>
          <section className="card">
            <div className="card-toolbar">
              <h2 className="card-title">Add category</h2>
            </div>
            <form className="form-grid" onSubmit={handleAddCategory}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={catForm.name}
                  onChange={(e) => {
                    setCatForm((f) => ({ ...f, name: e.target.value }));
                    setCatError(null);
                  }}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Description</span>
                <input
                  className="input"
                  value={catForm.description}
                  onChange={(e) =>
                    setCatForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </label>
              <div className="field field--actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={catSubmitting}
                >
                  {catSubmitting ? 'Saving…' : 'Save category'}
                </button>
              </div>
            </form>
            {catError && (
              <p className="modal-error" role="alert" style={{ marginTop: '0.6rem' }}>
                {catError}
              </p>
            )}
          </section>

          <section className="card">
            <div className="card-toolbar">
              <h2 className="card-title">Category list</h2>
            </div>
            <form
              className="form-grid"
              style={{ marginBottom: '0.85rem' }}
              onSubmit={applyCategorySearch}
            >
              <label className="field">
                <span className="field-label">Search (q)</span>
                <input
                  className="input"
                  value={catSearchInput}
                  onChange={(e) => setCatSearchInput(e.target.value)}
                  placeholder="Type to search…"
                  aria-label="Category search"
                />
              </label>
              <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
              <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={clearCategorySearch}
                >
                  Clear
                </button>
              </div>
            </form>
            {catListError && (
              <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
                {catListError}
              </p>
            )}
            {catActionError && (
              <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
                {catActionError}
              </p>
            )}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {catListLoading && catItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="empty-cell">
                        Loading…
                      </td>
                    </tr>
                  ) : !catListLoading && catItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="empty-cell">
                        {catListError
                          ? 'Could not load categories.'
                          : catSearchQ
                            ? 'No categories match your search.'
                            : 'No categories yet.'}
                      </td>
                    </tr>
                  ) : (
                    catItems.map((c) => (
                      <tr key={c.uuid || c.id}>
                        {catEditingId === c.id ? (
                          <>
                            <td>
                              <input
                                className="input input--inline"
                                value={catEditDraft.name}
                                onChange={(e) =>
                                  setCatEditDraft((d) => ({
                                    ...d,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="input input--inline"
                                value={catEditDraft.description}
                                onChange={(e) =>
                                  setCatEditDraft((d) => ({
                                    ...d,
                                    description: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td className="mono">
                              {c.updated_at || c.created_at || '—'}
                            </td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => commitCategoryEdit(c)}
                                disabled={catSubmitting || catDeletingId != null}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => setCatEditingId(null)}
                                disabled={catSubmitting || catDeletingId != null}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{c.name}</td>
                            <td>{c.description || '—'}</td>
                            <td className="mono">{c.updated_at || c.created_at || '—'}</td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost btn-icon"
                                onClick={() => startCategoryEdit(c)}
                                disabled={catDeletingId != null}
                                aria-label="Edit category"
                                title="Edit"
                              >
                                <IconEdit className="action-icon" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger btn-icon"
                                onClick={() => handleDeleteCategory(c)}
                                disabled={catDeletingId != null}
                                aria-busy={catDeletingId === String(c.id)}
                                aria-label={
                                  catDeletingId === String(c.id)
                                    ? 'Deleting category'
                                    : 'Delete category'
                                }
                                title={
                                  catDeletingId === String(c.id)
                                    ? 'Deleting…'
                                    : 'Delete'
                                }
                              >
                                {catDeletingId === String(c.id) ? (
                                  <IconSpinner className="action-icon action-icon--spin" />
                                ) : (
                                  <IconTrash className="action-icon" />
                                )}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {catMeta.total > 0 && (
              <Pagination
                page={catListPage}
                totalPages={catMeta.lastPage}
                onPageChange={setCatListPage}
                total={catMeta.total}
                from={catMeta.from}
                to={catMeta.to}
              />
            )}
          </section>
        </>
      )}

      {tab === 'products' && (
        <>
      {addOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={closeAddModal}
        >
          <div
            className="modal modal--wide"
            role="dialog"
            aria-labelledby="add-product-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="add-product-title" className="modal-title">
                New product
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={handleAddSubmit}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  autoFocus
                />
              </label>
              <label className="field">
                <span className="field-label">Category</span>
                <select
                  className="input"
                  value={form.categoryId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, categoryId: e.target.value }))
                  }
                  required
                  disabled={categoryLoading}
                >
                  <option value="">
                    {categoryLoading ? 'Loading…' : 'Select category'}
                  </option>
                  {categoryOptions.map((c) => (
                    <option key={c.uuid || c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {categoryLoadError && (
                  <p className="modal-error" role="alert">
                    {categoryLoadError}
                  </p>
                )}
              </label>
              <label className="field">
                <span className="field-label">Selling price (TZS)</span>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price: e.target.value }))
                  }
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Buying price (TZS)</span>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.buyingPrice}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, buyingPrice: e.target.value }))
                  }
                  placeholder="0"
                />
              </label>
              <label className="field">
                <span className="field-label">Reorder level</span>
                <input
                  className="input"
                  type="number"
                  step="1"
                  min="0"
                  value={form.reorderLevel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, reorderLevel: e.target.value }))
                  }
                  placeholder="0"
                />
              </label>
              <label className="field">
                <span className="field-label">Quantity</span>
                <input
                  className="input"
                  type="number"
                  step="1"
                  min="0"
                  value={form.quantity}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, quantity: e.target.value }))
                  }
                  placeholder="0"
                />
              </label>
              <label className="field">
                <span className="field-label">Registered date</span>
                <input
                  className="input"
                  type="date"
                  value={form.registeredDate}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      registeredDate: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              {productSubmitError && (
                <p className="modal-error" role="alert">
                  {productSubmitError}
                </p>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => !editSubmitting && closeEditModal()}
        >
          <div
            className="modal modal--wide"
            role="dialog"
            aria-labelledby="edit-product-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="edit-product-title" className="modal-title">
                Edit product
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => !editSubmitting && closeEditModal()}
                aria-label="Close"
                disabled={editSubmitting}
              >
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={handleEditSubmit}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  autoFocus
                  disabled={editSubmitting}
                />
              </label>
              <label className="field">
                <span className="field-label">Category</span>
                <select
                  className="input"
                  value={editForm.categoryId}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, categoryId: e.target.value }))
                  }
                  required
                  disabled={categoryLoading || editSubmitting}
                >
                  <option value="">
                    {categoryLoading ? 'Loading…' : 'Select category'}
                  </option>
                  {categoryOptions.map((c) => (
                    <option key={c.uuid || c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {categoryLoadError && (
                  <p className="modal-error" role="alert">
                    {categoryLoadError}
                  </p>
                )}
              </label>
              <label className="field">
                <span className="field-label">Selling price (TZS)</span>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.price}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, price: e.target.value }))
                  }
                  required
                  disabled={editSubmitting}
                />
              </label>
              <label className="field">
                <span className="field-label">Buying price (TZS)</span>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.buyingPrice}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, buyingPrice: e.target.value }))
                  }
                  placeholder="0"
                  disabled={editSubmitting}
                />
              </label>
              <label className="field">
                <span className="field-label">Reorder level</span>
                <input
                  className="input"
                  type="number"
                  step="1"
                  min="0"
                  value={editForm.reorderLevel}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, reorderLevel: e.target.value }))
                  }
                  placeholder="0"
                  disabled={editSubmitting}
                />
              </label>
              <label className="field">
                <span className="field-label">Quantity</span>
                <input
                  className="input"
                  type="number"
                  step="1"
                  min="0"
                  value={editForm.quantity}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, quantity: e.target.value }))
                  }
                  placeholder="0"
                  disabled={editSubmitting}
                />
              </label>
              <label className="field">
                <span className="field-label">Registered date</span>
                <input
                  className="input"
                  type="date"
                  value={editForm.registeredDate}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      registeredDate: e.target.value,
                    }))
                  }
                  required
                  disabled={editSubmitting}
                />
              </label>
              {editSubmitError && (
                <p className="modal-error" role="alert">
                  {editSubmitError}
                </p>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeEditModal}
                  disabled={editSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={editSubmitting}
                >
                  {editSubmitting ? 'Saving…' : 'Update product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">Product list</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setAddOpen(true)}
          >
            Add product
          </button>
        </div>
        <form
          className="form-grid"
          style={{ marginBottom: '0.85rem' }}
          onSubmit={applyProductSearch}
        >
          <label className="field">
            <span className="field-label">Search (q)</span>
            <input
              className="input"
              value={prodSearchInput}
              onChange={(e) => setProdSearchInput(e.target.value)}
              placeholder="Type to search…"
              aria-label="Product search"
            />
          </label>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </div>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearProductSearch}
            >
              Clear
            </button>
          </div>
        </form>
        {prodListError && (
          <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
            {prodListError}
          </p>
        )}
        {productActionError && (
          <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
            {productActionError}
          </p>
        )}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Buying price (TZS)</th>
                <th>Selling price (TZS)</th>
                <th>Quantity</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {prodListLoading && prodItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    Loading…
                  </td>
                </tr>
              ) : !prodListLoading && prodItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    {prodListError
                      ? 'Could not load products.'
                      : prodSearchQ
                        ? 'No products match your search.'
                        : 'No products yet. Add one with the button above.'}
                  </td>
                </tr>
              ) : (
                prodItems.map((row) => {
                  const cat = productCategoryLabelFromRow(row);
                  const active = row.can_be_sold !== 'no';
                  const selling = Number(row.selling_price) || 0;
                  const buying = Number(row.buying_price) || 0;
                  const bal = row.balance;
                  const qtyDisplay =
                    bal != null && bal !== '' && !Number.isNaN(Number(bal))
                      ? String(Number(bal))
                      : '—';
                  const rowKey = String(row.id);
                  return (
                    <tr key={row.uuid || row.id}>
                      <td>{row.name}</td>
                      <td>{cat}</td>
                      <td className="mono">{buying.toFixed(2)}</td>
                      <td className="mono">{selling.toFixed(2)}</td>
                      <td className="mono">{qtyDisplay}</td>
                      <td className="table-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            navigate(
                              `/sale?productId=${encodeURIComponent(String(row.id))}`
                            )
                          }
                          disabled={!active || deletingId != null}
                          title={
                            active
                              ? 'Open Sale and add this product'
                              : 'Inactive — cannot sell'
                          }
                        >
                          Sale
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost btn-icon"
                          onClick={() => openEditProduct(row)}
                          disabled={deletingId != null}
                          aria-label="Edit product"
                          title="Edit"
                        >
                          <IconEdit className="action-icon" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger btn-icon"
                          onClick={() => handleDeleteProduct(row)}
                          disabled={deletingId != null}
                          aria-busy={deletingId === rowKey}
                          aria-label={
                            deletingId === rowKey
                              ? 'Deleting product'
                              : 'Delete product'
                          }
                          title={
                            deletingId === rowKey ? 'Deleting…' : 'Delete'
                          }
                        >
                          {deletingId === rowKey ? (
                            <IconSpinner className="action-icon action-icon--spin" />
                          ) : (
                            <IconTrash className="action-icon" />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {prodItems.length > 0 && (
          <div className="pagination" role="navigation" aria-label="Product pagination">
            <span className="pagination-range">
              {(prodListPage - 1) * prodPerPage + 1}–
              {(prodListPage - 1) * prodPerPage + prodItems.length}
              {prodSearchQ ? ` · search "${prodSearchQ}"` : ''}
            </span>
            <div className="pagination-actions">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={prodListPage <= 1}
                onClick={() => setProdListPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                Previous
              </button>
              <span className="pagination-page mono">Page {prodListPage}</span>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={prodItems.length < prodPerPage}
                onClick={() => setProdListPage((p) => p + 1)}
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
        </>
      )}
    </div>
  );
}
